export type QuoteArtifactId = `atlas-x-${number}`;

export type QuoteArtifactAnchor =
  | {
      kind: "crystal";
      chapterSlug: string;
      crystalId: string;
      sourceField: "title" | "claim" | "mechanism";
      placement: "afterHeader" | "afterMechanism";
    }
  | {
      kind: "essayProposition";
      essaySlug: string;
      sectionId: string;
    }
  | {
      kind: "essayConclusion";
      essaySlug: string;
      paragraphIndex: number;
      sentenceIndex: number;
    };

export interface QuoteArtifactAsset {
  src: string;
  fullSrc: string;
  width: 768;
  height: 768;
}

export interface QuoteArtifact {
  id: QuoteArtifactId;
  anchor: QuoteArtifactAnchor;
  assets: {
    dark: QuoteArtifactAsset;
    light: QuoteArtifactAsset;
  };
}

function artifact(id: QuoteArtifactId, anchor: QuoteArtifactAnchor): QuoteArtifact {
  return {
    id,
    anchor,
    assets: {
      dark: {
        src: `/images/quote-artifacts/dark/${id}.webp`,
        fullSrc: `/images/quote-artifacts/full/dark/${id}.webp`,
        width: 768,
        height: 768,
      },
      light: {
        src: `/images/quote-artifacts/light/${id}.webp`,
        fullSrc: `/images/quote-artifacts/full/light/${id}.webp`,
        width: 768,
        height: 768,
      },
    },
  };
}

/**
 * One language-independent placement manifest for the sixty Atlas quote
 * artifacts. Content ids and essay structure are shared by EN, UK, and RU.
 */
export const quoteArtifacts = [
  artifact("atlas-x-001", { kind: "crystal", chapterSlug: "expertise", crystalId: "mastery-local-intuition", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-002", { kind: "essayProposition", essaySlug: "map-knows-it-is-map", sectionId: "feynman-public-integrity" }),
  artifact("atlas-x-003", { kind: "crystal", chapterSlug: "victory", crystalId: "outcome-new-incentives", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-004", { kind: "essayProposition", essaySlug: "power-to-define-reality", sectionId: "safe-path-for-bad-news" }),
  artifact("atlas-x-005", { kind: "crystal", chapterSlug: "cooperation", crystalId: "conflict-can-be-real-without-hostility", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-006", { kind: "essayProposition", essaySlug: "victory-changes-winner", sectionId: "self-limitation-after-victory" }),
  artifact("atlas-x-007", { kind: "crystal", chapterSlug: "systems", crystalId: "systems-metric-is-not-the-whole", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-008", { kind: "essayProposition", essaySlug: "there-is-no-last-arena", sectionId: "frankl-and-levi" }),
  artifact("atlas-x-009", { kind: "crystal", chapterSlug: "visibility", crystalId: "power-needs-protected-correction", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-010", { kind: "essayProposition", essaySlug: "strategy-as-refusal", sectionId: "opportunity-cost-made-visible" }),
  artifact("atlas-x-011", { kind: "crystal", chapterSlug: "limits", crystalId: "limit-no-moral-exam", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-012", { kind: "essayConclusion", essaySlug: "world-is-not-made-of-games", paragraphIndex: 1, sentenceIndex: 5 }),
  artifact("atlas-x-013", { kind: "crystal", chapterSlug: "reality", crystalId: "reality-claim-must-risk-correction", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-014", { kind: "essayProposition", essaySlug: "map-knows-it-is-map", sectionId: "popper-risk-of-error" }),
  artifact("atlas-x-015", { kind: "crystal", chapterSlug: "plural-values", crystalId: "values-means-shape-the-end", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-016", { kind: "essayProposition", essaySlug: "victory-changes-winner", sectionId: "success-creates-a-new-game" }),
  artifact("atlas-x-017", { kind: "crystal", chapterSlug: "finitude", crystalId: "finitude-care-changes-reality", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-018", { kind: "essayProposition", essaySlug: "there-is-no-last-arena", sectionId: "dignity-without-performance" }),
  artifact("atlas-x-019", { kind: "crystal", chapterSlug: "uncertainty", crystalId: "decision-process-and-outcome", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-020", { kind: "essayProposition", essaySlug: "strategy-as-refusal", sectionId: "diagnosis-before-ambition" }),
  artifact("atlas-x-021", { kind: "crystal", chapterSlug: "freedom", crystalId: "freedom-dependence-can-enable-agency", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-022", { kind: "essayProposition", essaySlug: "power-to-define-reality", sectionId: "categories-build-administrative-world" }),
  artifact("atlas-x-023", { kind: "crystal", chapterSlug: "risk", crystalId: "risk-protect-future-agency", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-024", { kind: "essayProposition", essaySlug: "world-is-not-made-of-games", sectionId: "ostrom-beyond-fixed-rules" }),
  artifact("atlas-x-025", { kind: "crystal", chapterSlug: "narrative-status", crystalId: "status-is-prior-not-verdict", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-026", { kind: "essayProposition", essaySlug: "map-knows-it-is-map", sectionId: "scott-legibility-and-closure" }),
  artifact("atlas-x-027", { kind: "crystal", chapterSlug: "organizational-learning", crystalId: "organization-bad-news-route", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-028", { kind: "essayProposition", essaySlug: "power-to-define-reality", sectionId: "arendt-foucault-common-action" }),
  artifact("atlas-x-029", { kind: "crystal", chapterSlug: "institutions", crystalId: "institutions-store-solutions-and-errors", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-030", { kind: "essayProposition", essaySlug: "strategy-as-refusal", sectionId: "machiavelli-effective-truth" }),
  artifact("atlas-x-031", { kind: "crystal", chapterSlug: "strategy", crystalId: "strategy-priority-requires-refusal", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-032", { kind: "essayProposition", essaySlug: "victory-changes-winner", sectionId: "identity-of-the-winner" }),
  artifact("atlas-x-033", { kind: "crystal", chapterSlug: "reality", crystalId: "reality-observation-has-blind-spots", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-034", { kind: "essayProposition", essaySlug: "there-is-no-last-arena", sectionId: "agency-is-not-a-switch" }),
  artifact("atlas-x-035", { kind: "crystal", chapterSlug: "games", crystalId: "games-model-response-not-only-move", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-036", { kind: "essayProposition", essaySlug: "world-is-not-made-of-games", sectionId: "schelling-conflict-and-coordination" }),
  artifact("atlas-x-037", { kind: "crystal", chapterSlug: "expertise", crystalId: "mastery-honest-feedback", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-038", { kind: "essayProposition", essaySlug: "map-knows-it-is-map", sectionId: "trace-measurement-story" }),
  artifact("atlas-x-039", { kind: "crystal", chapterSlug: "power-blindness", crystalId: "power-self-limitation-sensor", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-040", { kind: "essayProposition", essaySlug: "victory-changes-winner", sectionId: "distance-from-consequences" }),
  artifact("atlas-x-041", { kind: "crystal", chapterSlug: "plural-values", crystalId: "values-three-questions", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-042", { kind: "essayConclusion", essaySlug: "power-to-define-reality", paragraphIndex: 1, sentenceIndex: 5 }),
  artifact("atlas-x-043", { kind: "crystal", chapterSlug: "strategy", crystalId: "strategy-is-adaptive-causal-hypothesis", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-044", { kind: "essayProposition", essaySlug: "strategy-as-refusal", sectionId: "coherence-and-revision" }),
  artifact("atlas-x-045", { kind: "crystal", chapterSlug: "uncertainty", crystalId: "decision-preserve-future-agency", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-046", { kind: "essayProposition", essaySlug: "there-is-no-last-arena", sectionId: "relational-agency" }),
  artifact("atlas-x-047", { kind: "crystal", chapterSlug: "freedom", crystalId: "freedom-capability-not-menu", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-048", { kind: "essayProposition", essaySlug: "world-is-not-made-of-games", sectionId: "coercion-kant-and-arendt" }),
  artifact("atlas-x-049", { kind: "crystal", chapterSlug: "cooperation", crystalId: "cooperation-trust-is-specific-expectation", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-050", { kind: "essayConclusion", essaySlug: "map-knows-it-is-map", paragraphIndex: 2, sentenceIndex: 4 }),
  artifact("atlas-x-051", { kind: "crystal", chapterSlug: "narrative-status", crystalId: "narrative-selects-cause-and-action", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-052", { kind: "essayProposition", essaySlug: "power-to-define-reality", sectionId: "position-gives-words-weight" }),
  artifact("atlas-x-053", { kind: "crystal", chapterSlug: "reality", crystalId: "reality-trace-before-story", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-054", { kind: "essayProposition", essaySlug: "strategy-as-refusal", sectionId: "berlin-pluralism-and-kantian-limit" }),
  artifact("atlas-x-055", { kind: "crystal", chapterSlug: "games", crystalId: "games-model-response-not-only-move", sourceField: "mechanism", placement: "afterMechanism" }),
  artifact("atlas-x-056", { kind: "essayConclusion", essaySlug: "victory-changes-winner", paragraphIndex: 2, sentenceIndex: 0 }),
  artifact("atlas-x-057", { kind: "crystal", chapterSlug: "victory", crystalId: "outcome-stable-settlement", sourceField: "title", placement: "afterHeader" }),
  artifact("atlas-x-058", { kind: "essayConclusion", essaySlug: "there-is-no-last-arena", paragraphIndex: 2, sentenceIndex: 2 }),
  artifact("atlas-x-059", { kind: "crystal", chapterSlug: "power-blindness", crystalId: "power-intellect-advocate", sourceField: "claim", placement: "afterHeader" }),
  artifact("atlas-x-060", { kind: "essayProposition", essaySlug: "world-is-not-made-of-games", sectionId: "no-single-hierarchy-of-games" }),
] satisfies readonly QuoteArtifact[];

export function artifactsForCrystal(
  chapterSlug: string,
  crystalId: string,
  placement: Extract<QuoteArtifactAnchor, { kind: "crystal" }>["placement"],
) {
  return quoteArtifacts.filter((entry) => entry.anchor.kind === "crystal"
    && entry.anchor.chapterSlug === chapterSlug
    && entry.anchor.crystalId === crystalId
    && entry.anchor.placement === placement);
}

export function artifactForEssayProposition(essaySlug: string, sectionId: string) {
  return quoteArtifacts.find((entry) => entry.anchor.kind === "essayProposition"
    && entry.anchor.essaySlug === essaySlug
    && entry.anchor.sectionId === sectionId);
}

export function artifactsForEssayConclusion(essaySlug: string, paragraphIndex: number) {
  return quoteArtifacts.filter((entry) => entry.anchor.kind === "essayConclusion"
    && entry.anchor.essaySlug === essaySlug
    && entry.anchor.paragraphIndex === paragraphIndex);
}
